import { getLink } from "@utils/linkUtils";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { FC, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import FocusLock from "react-focus-lock";

import HamburgerMenu from "@/components/Header/HamburgerMenu";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
// import Image from "next/image";
import Image from "@/components/ui/Image/Image";
import Modal from "@/components/ui/Modal/Modal";
import ModalContent from "@/components/ui/Modal/ModalContent";
import { ModalsContext } from "@/context/ModalsContext";
import { NavigationContext } from "@/context/NavigationContext";
import { TemplateContext } from "@/context/TemplateContext";
import { UTMParametersContext, getFallbackCampaignParameters } from "@/context/UTMParametersContext";
import { UserContext } from "@/context/UserContext";
import { browsePropertiesHeaderLinkClick, ctaClickTracking, navigationClickTracking, searchInteractionTracking } from "@/dataLayer/dataLayer";
import useScroll from "@/hooks/useScroll";
import { HeaderProps } from "@/types";

import SoftLogin from "../SoftLogin/SoftLogin";

const LocationMobile = dynamic(() => import("@/components/ui/Form/Filters/LocationMobile"), { ssr: false });
const ContactChannels = dynamic(() => import("@/components/ContactChannels/ContactChannels"), { ssr: false });

const whiteMenu = {
  container: "top-0",
  containerBg: "bg-white",
  darkLogo: "block",
  lightLogo: "hidden",
  iconStroke: "stroke-black",
  hamburgerVariant: "dark",
};

const transparentMenu = {
  container: "top-0",
  containerBg: "bg-transparent",
  darkLogo: "hidden",
  lightLogo: "block",
  iconStroke: "stroke-white",
  hamburgerVariant: "light",
};

const HeaderMobile: FC<HeaderProps> = ({
  logoDark,
  logoLight,
  menu,
  personalArea,
  shortlist,
  contactChannels,
  search,
  getInTouch,
  onLogin,
  settings,
}) => {
  const scroll = useScroll();
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();

  const { isLoggedIn } = useContext(UserContext);
  const { fixedHeader, setVisibleHeader } = useContext(TemplateContext);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const menuItemRef = useRef<(HTMLDivElement | null)[]>([]);
  const headerMobileRef = useRef<HTMLDivElement>(null);

  const {
    mobileMenuOpen,
    setMobileMenuOpen,
    mobilePanelID,
    setMobilePanelID,
    settingsVisible,
    setSettingsVisible,
    setLanguagesVisible,
    languagesVisible,
    transitionComplete,
    setMobileHeaderHeight,
  } = useContext(NavigationContext);

  const { checkLoginError } = useContext(UserContext);

  // scroll assume un valore tra -1 e 1 a seconda della direzione
  // 0 significa che sono al top
  const scrollClasses: Record<string, string> = useMemo(() => {
    if (scroll === 0) {
      if (fixedHeader) {
        return pathname?.includes("terms-and-conditions") || pathname?.includes("privacy-policy") ? whiteMenu : transparentMenu;
      } else {
        return whiteMenu;
      }
    } else if (scroll > 0) {
      // @Valerio
      //This throws a client error, i don't know if is needed
      setVisibleHeader(false);
      return {
        ...whiteMenu,
        container: `${pathname?.includes("terms-and-conditions") || pathname?.includes("privacy-policy") ? "top-[0]" : "top-[-100%]"}`,
      };
    } else {
      // @Valerio
      //This throws a client error, i don't know if is needed
      setVisibleHeader(true);
      return { ...whiteMenu, containerBg: "shadow-strong bg-white" };
    }
  }, [scroll, fixedHeader]);

  const scrollClass: string = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.scrollY > 0 ? "scroll" : "noscroll";
    }
    return "noscroll"; // Default se window non è definito
  }, [scroll]);

  const callLink = useMemo(() => {
    return contactChannels?.find((item) => /phone/.test(`${item?.link?.icon}`));
  }, [contactChannels]);

  const gitInTouchLink = useMemo(() => {
    return contactChannels?.find((item) => item?.label === "Get in touch");
  }, [contactChannels]);

  const languageCallback = useCallback(() => {
    if (settingsVisible) {
      setLanguagesVisible(languagesVisible);
    }
    setLanguagesVisible(!languagesVisible);
    ctaClickTracking(t("header.label.languages"), "HeaderMobile", "HeaderMobile");
  }, [settingsVisible, languagesVisible, setLanguagesVisible]);

  const settingsCallback = useCallback(() => {
    if (languagesVisible) {
      setLanguagesVisible(settingsVisible);
    }
    setSettingsVisible(!settingsVisible);
    ctaClickTracking(t("header.label.settings"), "HeaderMobile", "HeaderMobile");
  }, [settingsVisible, languagesVisible, setLanguagesVisible, setSettingsVisible]);

  useEffect(() => {
    setMobileHeaderHeight(headerMobileRef?.current?.offsetHeight ?? 0);
  }, [headerMobileRef, setMobileHeaderHeight]);

  const variants = {
    open: { x: 0, display: "block" },
    close: { x: "100vw", transitionEnd: { display: "none" } },
  };
  const transition = {
    duration: 0.5,
  };

  const { utmParameters } = useContext(UTMParametersContext);

  return (
    <>
      <div
        ref={headerMobileRef}
        id="header-mobile"
        className={`${scrollClasses?.container} ${scrollClasses?.containerBg} ${fixedHeader ? "fixed" : "fixed"} w-full transition-all ease-in-out duration-500 backdrop-blur-md z-20 md:hidden ${scrollClass}`}
      >
        <div className="flex items-center justify-between px-m py-xs">
          <div className="flex items-center">
            <HamburgerMenu
              ref={hamburgerRef}
              variant={scrollClasses?.hamburgerVariant}
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
            />
            {search ? (
              <Button
                rounded
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NONE}
                type="button"
                variant={ButtonVariant.PRIMARY}
                customClass="border-0 bg-transparent"
                onClick={() => {
                  setMobilePanelID("location");
                  searchInteractionTracking();
                  ctaClickTracking("Search", "HeaderMobile", "HeaderMobile");
                }}
              >
                <span className="flex items-center gap-xs">
                  {/* <Icon className={scrollClasses.iconStroke} name={search.link.icon ?? ""} size="20" title={search.label} /> */}
                  <Icon className={scrollClasses?.iconStroke} name="search" size="24" title={search?.label} />
                </span>
              </Button>
            ) : (
              <div className="w-l"></div>
            )}
          </div>
          <AnchorLink href={`/${locale}/`} aria-label={t("button.label.homepage")}>
            <Image
              fit="pad"
              className={`m-auto max-w-[133px] md:max-w-[170px] md:m-0 h-xl ${scrollClasses?.darkLogo}`}
              src={logoDark?.url ?? ""}
              width={170}
              height={48}
              alt={logoDark?.title}
              loading="eager"
            />
            <Image
              fit="pad"
              className={`m-auto max-w-[133px] md:max-w-[170px] md:m-0 h-xl ${scrollClasses?.lightLogo}`}
              src={logoLight?.url}
              width={170}
              height={48}
              alt={logoDark?.title}
              loading="eager"
            />
          </AnchorLink>
          <div className="flex items-center">
            {
              <Button
                rounded
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NONE}
                type="button"
                variant={ButtonVariant.PRIMARY}
                customClass="border-0 bg-transparent"
                // href={callLink.link.url}
                href={`tel:${getFallbackCampaignParameters(utmParameters?.utm_medium)?.virtual_number || "+97145205400"}`}
              >
                <span className="flex items-center gap-xs">
                  <Icon className={scrollClasses?.iconStroke} name={callLink?.link?.icon || "phone"} size="24" title={callLink?.label} />
                </span>
              </Button>
            }
            {(getInTouch || gitInTouchLink) && (
              <Button
                rounded
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NONE}
                type="button"
                variant={ButtonVariant.PRIMARY}
                customClass="border-0 bg-transparent"
              >
                <span className="flex items-center gap-xs">
                  {getInTouch?.link?.icon ? (
                    <Icon className={scrollClasses.iconStroke} name={getInTouch?.link?.icon} size="24" title={getInTouch?.label} />
                  ) : (
                    gitInTouchLink?.link?.icon && (
                      <Icon className={scrollClasses?.iconStroke} name={gitInTouchLink?.link?.icon} size="24" title={gitInTouchLink?.label} />
                    )
                  )}
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Main header mobile */}
      {/* Main menu mobile */}
      <motion.div
        id="mobile-panel-main"
        initial={variants.close}
        transition={transition}
        variants={variants}
        animate={mobileMenuOpen ? "open" : "close"}
        className={`fixed z-40 left-0 top-0 w-screen h-dvh bg-white `}
      >
        <FocusLock
          disabled={!mobileMenuOpen}
          className="h-full flex flex-col"
          autoFocus={true}
          group="main"
          returnFocus={() => {
            hamburgerRef?.current?.focus();
            return false;
          }}
        >
          <header className="flex justify-between items-center p-m">
            <p className="uppercase text-m overflow-hidden whitespace-nowrap text-ellipsis">{menu?.name}</p>
            <button
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
            >
              <Icon name="close" className="stroke-black" size="24" title={t("label.common.close")} />
            </button>
          </header>
          <div className="flex-1 overflow-auto py-xl px-m">
            <nav className="flex flex-col">
              {menu?.items?.map((item, index) => {
                return (
                  item.title && (
                    <div
                      key={item.id + `-mobile-` + index}
                      ref={(el) => {
                        menuItemRef.current[index] = el;
                      }}
                    >
                      <Button
                        className="px-s w-full py-s no-underline flex justify-between items-center"
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.SMALL}
                        theme={ButtonTheme.BLACK}
                        type="button"
                        onClick={() => {
                          setMobilePanelID(item.id);
                          navigationClickTracking(item.title);
                          // setLastClick(index);
                        }}
                      >
                        <>
                          {item.title}
                          <Icon className="stroke-black" name="arrow-right" title={t("label.common.arrow")} size="16" />
                        </>
                      </Button>
                    </div>
                  )
                );
              })}
            </nav>
            <hr className="my-xl border-white border-b border-b-gray-10" />
            <ContactChannels contactChannels={contactChannels} />
          </div>
          <footer className="grid grid-flow-col auto-cols-fr border-t border-t-gray-10">
            {personalArea && personalArea?.link && (
              <AnchorLink className="hidden py-m text-center" href={getLink(isLoggedIn ? personalArea : "", locale).toString()} span={!isLoggedIn}>
                <div className="flex flex-col items-center p-m">
                  <SoftLogin
                    icon={{ notLoggedIcon: personalArea?.link?.icon!, loggedIcon: "user-selected" }}
                    labelClass="font-secondary text-s mt-s"
                    label={personalArea?.label}
                  />
                </div>
              </AnchorLink>
            )}
            {shortlist && shortlist.link && (
              <AnchorLink className="py-m" href={getLink(shortlist, locale).toString()}>
                <div className="flex flex-col items-center p-m">
                  <Icon name={shortlist?.link?.icon || "heart-empty"} size="24" className="stroke-black" />
                  <span className="font-secondary text-s mt-s">{shortlist?.label}</span>
                </div>
              </AnchorLink>
            )}

            <Button
              variant={ButtonVariant.TERTIARY}
              size={ButtonSize.SMALL}
              theme={ButtonTheme.BLACK}
              type="button"
              className="py-m"
              disabled={!transitionComplete}
              onClick={settingsCallback}
            >
              <div className="flex flex-col items-center">
                <Icon className="stroke-black" name="settings" size="24" />
                <span className="font-secondary text-s mt-s">{t("header.label.settings")}</span>
              </div>
            </Button>
            {settings?.settings?.languages && settings?.settings?.languages?.length > 1 && (
              <Button
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.SMALL}
                theme={ButtonTheme.BLACK}
                type="button"
                className="py-m"
                disabled={!transitionComplete}
                onClick={languageCallback}
              >
                <div className="flex flex-col items-center">
                  <Icon className="stroke-black" name="flag" size="20" />
                  <span className="font-secondary text-s mt-s">{t("header.label.languages")}</span>
                </div>
              </Button>
            )}
          </footer>
        </FocusLock>
      </motion.div>
      {/* Main menu mobile */}
      {/* Menu mobile panels */}
      {menu?.items?.map((item) => {
        const active = mobilePanelID && mobilePanelID === item.id;
        return (
          <motion.div
            id={`mobile-panel-mobile-${item.id}`}
            initial={variants.close}
            transition={transition}
            variants={variants}
            animate={active ? "open" : "close"}
            key={`subpanel-mobile-${item.id}`}
            className={`translate-x-[100%] fixed z-40 left-0 top-0 w-screen h-dvh bg-white `}
          >
            <FocusLock group="menu" disabled={!active} className="w-full h-full overflow-hidden">
              <div className="flex flex-col h-full">
                <header className="flex gap-s justify-between items-center p-m shrink-0">
                  <button
                    onClick={() => {
                      setMobilePanelID("");
                    }}
                  >
                    <Icon className="stroke-black" name="arrow-left" title={t("label.common.arrow")} size="24" />
                  </button>
                  <p className="uppercase text-m font-primary overflow-hidden whitespace-nowrap text-ellipsis">{item.title}</p>
                  <button
                    onClick={() => {
                      setMobilePanelID("");
                      setMobileMenuOpen(false);
                    }}
                  >
                    <Icon name="close" className="stroke-black" size="24" title={t("label.common.close")} />
                  </button>
                </header>
                <section className="flex-1 overflow-auto">
                  {item?.columns?.map((column, index) => {
                    return (
                      <div key={`column-${index}-${column?.id}`} className="py-m px-m">
                        <p className="py-s mb-xs text-s uppercase text-gray-50 font-primary font-medium">{column?.title}</p>
                        <ul>
                          {column?.links?.map((link, lindex) => {
                            return (
                              <li key={`mobile-menu-link-${link?.id}`}>
                                <Button
                                  className={`${menu?.highlightFirstCluster && index === 0 ? "text-sm py-s" : "text-sm py-xs"} w-full no-underline flex justify-between items-center font-secondary font-medium`}
                                  variant={ButtonVariant.TERTIARY}
                                  size={ButtonSize.SMALL}
                                  theme={ButtonTheme.BLACK}
                                  type="button"
                                  href={getLink(link, locale)}
                                  onClick={() => browsePropertiesHeaderLinkClick({ link_category: column?.title, link_name: link?.label })}
                                >
                                  {link?.label}
                                </Button>
                              </li>
                            );
                          })}
                          {column?.viewAll && (
                            <li>
                              <Button
                                className={`${menu?.highlightFirstCluster && index === 0 ? "text-sm py-s" : "text-m py-xs"} w-full no-underline flex justify-between items-center font-secondary font-medium`}
                                variant={ButtonVariant.TERTIARY}
                                size={ButtonSize.SMALL}
                                theme={ButtonTheme.BLACK}
                                href={getLink(column.viewAll, locale)}
                                onClick={() =>
                                  browsePropertiesHeaderLinkClick({ link_category: column?.title, link_name: column?.viewAll?.label || "" })
                                }
                              >
                                {column?.viewAll?.label}
                              </Button>
                            </li>
                          )}
                        </ul>
                      </div>
                    );
                  })}
                </section>
                {item?.menuItemCta?.link && (
                  <footer className="shrink-0 p-s text-center">
                    <Button
                      href={getLink(item?.menuItemCta, locale)}
                      customClass="inline-block"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.MEDIUM}
                      theme={ButtonTheme.BLACK}
                      type="button"
                    >
                      {item?.menuItemCta?.label}
                    </Button>
                  </footer>
                )}
              </div>
            </FocusLock>
          </motion.div>
        );
      })}
      {/* Menu mobile panels */}
      {/* Location Search Mobile */}

      <Modal topPositioned className="md:hidden" open={mobilePanelID === "location"} onClose={() => setMobilePanelID("")}>
        <ModalContent removePadding className="w-full md:hidden h-dvh rounded-none rounded-t-none">
          <LocationMobile />
        </ModalContent>
      </Modal>
    </>
  );
};
export default HeaderMobile;
